/**
 * Phase 9.1 — WhatsApp Cloud API Go-Live Readiness Dashboard.
 *
 * Pure observability + manual template-checklist UI. Does NOT toggle
 * WHATSAPP_CLOUD_API_ENABLED, never triggers a live Meta send, and never
 * touches Gupshup. All four guardrails (kill switch, canary, dry-run,
 * blocked) are surfaced as read-only state.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  CheckCircle, WarningCircle, XCircle, Info, Circle,
  ShieldCheck, ShieldWarning, Lightning, ArrowClockwise,
} from "@phosphor-icons/react";

const MODE_TONE = {
  dry_run: { label: "Dry-run",  cls: "border-amber-500/40 bg-amber-500/10 text-amber-300", dot: "bg-amber-400" },
  canary:  { label: "Canary",   cls: "border-sky-500/40 bg-sky-500/10 text-sky-300",     dot: "bg-sky-400"   },
  live:    { label: "Live",     cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", dot: "bg-emerald-400" },
  blocked: { label: "Blocked",  cls: "border-red-500/40 bg-red-500/10 text-red-300",      dot: "bg-red-500"   },
};

const TEMPLATE_STATES = ["not_submitted", "submitted", "approved", "rejected", "needs_edit"];

const STATE_TONE = {
  approved:      "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  submitted:     "text-sky-300 bg-sky-500/10 border-sky-500/30",
  rejected:      "text-red-300 bg-red-500/10 border-red-500/30",
  needs_edit:    "text-amber-300 bg-amber-500/10 border-amber-500/30",
  not_submitted: "text-white/55 bg-white/5 border-white/15",
};

function Pill({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data tracking-widest uppercase border ${
        ok ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
           : "border-red-500/40 text-red-300 bg-red-500/10"
      }`}
      data-testid={`pill-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {ok ? <CheckCircle size={11} weight="bold" /> : <XCircle size={11} weight="bold" />}
      {label}
    </span>
  );
}

function Section({ title, children, action }) {
  return (
    <section className="border border-[#2A2A2A] bg-[#101010]" data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A2A]">
        <h2 className="text-[11px] font-display tracking-widest uppercase font-bold text-white/85">{title}</h2>
        {action}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function AdminWaCloud() {
  const [status, setStatus] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTpl, setSavingTpl] = useState(null);
  const [error, setError] = useState(null);
  const [subCheck, setSubCheck] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState(null);
  const [inbound, setInbound] = useState(null);
  const [inboundLoading, setInboundLoading] = useState(false);
  const [inboundError, setInboundError] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c, a] = await Promise.all([
        api.get("/admin/wa-cloud/status"),
        api.get("/admin/wa-cloud/template-checklist"),
        api.get("/admin/wa-cloud/send-audit?limit=20"),
      ]);
      setStatus(s.data);
      setChecklist(c.data);
      setAudit(a.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const runSubscriptionCheck = async () => {
    setSubLoading(true);
    setSubError(null);
    try {
      const { data } = await api.get("/admin/wa-cloud/meta-subscription-check");
      setSubCheck(data);
    } catch (e) {
      setSubError(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setSubLoading(false);
    }
  };

  const runInboundDebug = async () => {
    setInboundLoading(true);
    setInboundError(null);
    try {
      const { data } = await api.get("/admin/wa-cloud/inbound-debug?limit=5");
      setInbound(data);
    } catch (e) {
      setInboundError(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setInboundLoading(false);
    }
  };

  const updateTemplate = async (template_name, state) => {
    setSavingTpl(template_name);
    try {
      await api.patch("/admin/wa-cloud/template-checklist", { template_name, state });
      const c = await api.get("/admin/wa-cloud/template-checklist");
      setChecklist(c.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSavingTpl(null);
    }
  };

  const mode = status?.active_channel || "dry_run";
  const tone = MODE_TONE[mode] || MODE_TONE.dry_run;

  const readinessChecks = useMemo(() => {
    if (!status) return [];
    return [
      { label: "Environment is production",     ok: status.app_env === "production",   info: `Detected: ${status.app_env}` },
      { label: "META_APP_SECRET configured",    ok: status.has_meta_app_secret },
      { label: "WHATSAPP_VERIFY_TOKEN set",     ok: status.has_verify_token },
      { label: "WHATSAPP_ACCESS_TOKEN set",     ok: status.has_access_token },
      { label: "WHATSAPP_PHONE_NUMBER_ID set",  ok: status.has_phone_number_id },
      { label: "WhatsApp Business Account ID",  ok: status.has_waba_id },
      { label: "Webhook signature verification active", ok: status.signature_verification_active },
      { label: "Cloud API enabled (master switch)",     ok: status.enabled },
    ];
  }, [status]);

  return (
    <div className="space-y-5" data-testid="wa-cloud-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="qd-overline text-[10px] mb-1">Phase 9.1 · Operations</div>
          <h1 className="text-2xl font-display tracking-wide text-white">WhatsApp Cloud API · Go-Live Readiness</h1>
          <p className="text-xs text-white/55 mt-1 max-w-2xl">
            Read-only safety dashboard. Toggling production traffic requires editing backend
            environment variables and a redeploy — no UI control surfaces that risk.
          </p>
        </div>
        <button
          data-testid="wa-cloud-refresh-btn"
          onClick={loadAll}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 border border-[#2A2A2A] hover:border-white/40 text-[11px] font-display tracking-widest uppercase text-white/70 hover:text-white disabled:opacity-50"
        >
          <ArrowClockwise size={14} weight="bold" /> Refresh
        </button>
      </div>

      {error && (
        <div data-testid="wa-cloud-error" className="border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {String(error)}
        </div>
      )}

      {/* Mode + readiness banners */}
      {status && (
        <div className="grid sm:grid-cols-3 gap-3">
          <div
            data-testid="active-channel-card"
            data-mode={mode}
            className={`border ${tone.cls} px-4 py-3`}
          >
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/55">Active Channel</div>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-block w-2 h-2 rounded-full ${tone.dot}`} />
              <span className="text-lg font-display tracking-wide">{tone.label}</span>
            </div>
            {mode === "blocked" && status.live_sending_block_reason && (
              <div className="text-[11px] text-red-300/85 mt-2 leading-snug">
                {status.live_sending_block_reason}
              </div>
            )}
            {mode === "canary" && (
              <div className="text-[11px] text-sky-300/85 mt-2 leading-snug">
                Only {status.canary_phones_count} whitelisted phone(s) can receive live sends.
              </div>
            )}
            {mode === "dry_run" && (
              <div className="text-[11px] text-amber-200/85 mt-2 leading-snug">
                WHATSAPP_CLOUD_API_ENABLED=false. No Meta calls are made.
              </div>
            )}
            {mode === "live" && (
              <div className="text-[11px] text-emerald-300/85 mt-2 leading-snug">
                Production-safe live traffic enabled. Audit every send.
              </div>
            )}
          </div>

          <div className="border border-[#2A2A2A] bg-[#101010] px-4 py-3" data-testid="ready-for-canary-card">
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/55">Ready for Canary</div>
            <div className="flex items-center gap-2 mt-2">
              {status.ready_for_canary
                ? <ShieldCheck size={20} className="text-emerald-400" weight="bold" />
                : <ShieldWarning size={20} className="text-amber-400" weight="bold" />}
              <span className={`text-lg font-display tracking-wide ${status.ready_for_canary ? "text-emerald-300" : "text-amber-300"}`}>
                {status.ready_for_canary ? "YES" : "NO"}
              </span>
            </div>
            <div className="text-[11px] text-white/55 mt-1 leading-snug">
              Requires: prod env, all credentials, canary on, ≥1 whitelisted phone.
            </div>
          </div>

          <div className="border border-[#2A2A2A] bg-[#101010] px-4 py-3" data-testid="ready-for-live-card">
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/55">Ready for Live</div>
            <div className="flex items-center gap-2 mt-2">
              {status.ready_for_live
                ? <Lightning size={20} className="text-emerald-400" weight="bold" />
                : <Lightning size={20} className="text-white/30" weight="bold" />}
              <span className={`text-lg font-display tracking-wide ${status.ready_for_live ? "text-emerald-300" : "text-white/55"}`}>
                {status.ready_for_live ? "YES" : "NO"}
              </span>
            </div>
            <div className="text-[11px] text-white/55 mt-1 leading-snug">
              Requires: prod env, all credentials, canary OFF, enabled=true.
            </div>
          </div>
        </div>
      )}

      {/* Readiness checklist */}
      {status && (
        <Section title="Configuration Readiness">
          <div className="grid sm:grid-cols-2 gap-2">
            {readinessChecks.map((c) => (
              <div
                key={c.label}
                className="flex items-start gap-2 px-3 py-2 border border-[#222] bg-black/30"
                data-testid={`readiness-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {c.ok
                  ? <CheckCircle size={14} weight="bold" className="text-emerald-400 mt-0.5 shrink-0" />
                  : <XCircle size={14} weight="bold" className="text-red-400 mt-0.5 shrink-0" />}
                <div className="flex-1">
                  <div className="text-xs text-white/85">{c.label}</div>
                  {c.info && <div className="text-[10px] text-white/40 font-mono-data">{c.info}</div>}
                </div>
              </div>
            ))}
          </div>

          {status.missing_config?.length > 0 && (
            <div className="mt-3 border border-red-500/30 bg-red-500/5 px-3 py-2" data-testid="missing-config-list">
              <div className="text-[10px] font-display tracking-widest uppercase text-red-300 mb-1">
                Missing production config ({status.missing_config.length})
              </div>
              <div className="text-[11px] text-white/70 font-mono-data">
                {status.missing_config.join(" · ")}
              </div>
            </div>
          )}

          <div className="mt-3 grid sm:grid-cols-2 gap-3 text-[11px]">
            <div className="border border-[#222] bg-black/30 px-3 py-2" data-testid="canary-config-card">
              <div className="text-[10px] font-display tracking-widest uppercase text-white/55">Canary Mode</div>
              <div className="mt-1 flex items-center gap-2">
                <Pill ok={status.canary_enabled} label={status.canary_enabled ? "Enabled" : "Disabled"} />
                <span className="text-white/65">{status.canary_phones_count} whitelisted</span>
              </div>
              {status.canary_phones_preview?.length > 0 && (
                <div className="text-[10px] text-white/45 mt-1 font-mono-data">
                  {status.canary_phones_preview.join(", ")}
                </div>
              )}
            </div>
            <div className="border border-[#222] bg-black/30 px-3 py-2" data-testid="webhook-config-card">
              <div className="text-[10px] font-display tracking-widest uppercase text-white/55">Webhook</div>
              <div className="mt-1 flex items-center gap-2">
                <Pill ok={status.webhook_mounted} label={status.webhook_mounted ? "Mounted" : "Not mounted"} />
                <Pill ok={status.signature_verification_active} label="HMAC" />
              </div>
              <div className="text-[10px] text-white/45 mt-1 font-mono-data break-all">
                {status.webhook_url_hint}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Webhook verification handshake diagnostics */}
      {status?.verify_handshake && (
        <Section title="Webhook Verification Handshake">
          <div className="grid sm:grid-cols-2 gap-3 text-[11px]">
            <div className="border border-[#222] bg-black/30 px-3 py-2" data-testid="verify-config-card">
              <div className="text-[10px] font-display tracking-widest uppercase text-white/55">Configuration</div>
              <div className="mt-1 space-y-1">
                <Pill ok={status.verify_handshake.verify_token_configured}
                      label={status.verify_handshake.verify_token_configured ? "Verify token configured" : "Verify token MISSING"} />
                <div className="mt-1">
                  <Pill ok={status.webhook_mounted} label={status.webhook_mounted ? "Webhook route mounted" : "Webhook route NOT mounted"} />
                </div>
                <div className="text-[10px] text-white/45 font-mono-data">
                  Token length (server): {status.verify_handshake.verify_token_length}
                </div>
                <div className="text-[10px] text-white/45 font-mono-data">
                  Attempts recorded: {status.verify_handshake.attempts_recorded}
                </div>
                <div className="text-[10px] text-white/45 font-mono-data">
                  Raw hits total: {status.verify_handshake.raw_hits_total ?? 0}
                  {" · "}last GET: {status.verify_handshake.last_webhook_get?.ts?.slice(0, 19).replace("T", " ") || "—"}
                  {" · "}last POST: {status.verify_handshake.last_webhook_post?.ts?.slice(0, 19).replace("T", " ") || "—"}
                </div>
                {status.verify_handshake.raw_hits_total === 0 && (
                  <div className="text-[10px] text-amber-300/85 mt-1 leading-snug">
                    Zero raw hits → request is not reaching the FastAPI app. Curl <span className="font-mono-data">/api/wa-cloud/webhook-health</span> from your laptop. If that records a hit but Meta still doesn&apos;t, the issue is in Meta App config (callback URL, app subscribed, WABA permissions).
                  </div>
                )}
              </div>
            </div>
            <div className="border border-[#222] bg-black/30 px-3 py-2" data-testid="verify-last-attempt-card">
              <div className="text-[10px] font-display tracking-widest uppercase text-white/55">Last attempt received</div>
              {status.verify_handshake.last_attempt ? (
                <div className="mt-1 space-y-1">
                  <div className="text-[11px] text-white/80 font-mono-data">{status.verify_handshake.last_attempt.received_at?.slice(0, 19).replace("T", " ")}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Pill ok={status.verify_handshake.last_attempt.hub_mode_match}
                          label={`mode=${status.verify_handshake.last_attempt.hub_mode || "(none)"}`} />
                    <Pill ok={status.verify_handshake.last_attempt.hub_verify_token_present}
                          label={status.verify_handshake.last_attempt.hub_verify_token_present ? "token sent" : "no token"} />
                    <Pill ok={status.verify_handshake.last_attempt.hub_verify_token_match}
                          label={status.verify_handshake.last_attempt.hub_verify_token_match ? "token MATCH" : "token MISMATCH"} />
                    <Pill ok={status.verify_handshake.last_attempt.hub_challenge_present}
                          label={status.verify_handshake.last_attempt.hub_challenge_present ? "challenge present" : "no challenge"} />
                  </div>
                  <div className="text-[10px] text-white/45 font-mono-data">
                    inbound token len = {status.verify_handshake.last_attempt.hub_verify_token_length || 0}
                    {" · "}sha6 = {status.verify_handshake.last_attempt.hub_verify_token_hash6 || "—"}
                  </div>
                  <div className="text-[10px] text-white/55 font-mono-data">
                    Result: HTTP {status.verify_handshake.last_attempt.result_http_status}
                  </div>
                </div>
              ) : (
                <div className="text-[11px] text-white/40 italic mt-1">
                  No verification attempts received yet. When Meta hits the webhook the result will appear here.
                </div>
              )}
            </div>
          </div>
          <div className="text-[10px] text-white/40 font-mono-data mt-3 leading-snug">
            Q Drives never persists the inbound verify_token value — only booleans, length, and a 6-char sha256 prefix
            to let you spot whitespace or quote mismatches without exposing secrets.
          </div>
        </Section>
      )}

      {/* Meta Subscription Check — temporary diagnostic */}
      <Section
        title="Meta Subscription Check"
        action={
          <button
            data-testid="meta-subscription-check-btn"
            onClick={runSubscriptionCheck}
            disabled={subLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display tracking-widest uppercase border border-[#FF1E1E] text-[#FF1E1E] hover:bg-[#FF1E1E]/10 disabled:opacity-50"
          >
            {subLoading ? "Checking…" : "Run Meta Subscription Check"}
          </button>
        }
      >
        <div className="text-[11px] text-white/55 mb-3 leading-snug">
          Read-only diagnostic. Calls Meta Graph API with the existing access token to verify whether the
          App is subscribed to receive webhook events for this WABA. Helps diagnose missing inbound POSTs to
          <span className="font-mono-data"> /api/wa-cloud/webhook</span>. No config is changed.
        </div>

        {subError && (
          <div data-testid="meta-subscription-error" className="border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {String(subError)}
          </div>
        )}

        {subCheck && (
          <div className="space-y-3" data-testid="meta-subscription-result">
            {/* Summary */}
            <div className="grid sm:grid-cols-2 gap-2 text-[11px]">
              <div className="border border-[#222] bg-black/30 px-3 py-2" data-testid="meta-subscription-summary">
                <div className="text-[10px] font-display tracking-widest uppercase text-white/55 mb-1">Summary</div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Pill ok={subCheck.summary?.waba_reachable} label={`WABA reachable`} />
                    <Pill ok={subCheck.summary?.phone_number_reachable} label={`Phone reachable`} />
                    <Pill ok={subCheck.summary?.subscribed_apps_reachable} label={`subscribed_apps reachable`} />
                  </div>
                  <div className="text-[11px] text-white/85 font-mono-data">
                    subscribed_apps_count = <span className={subCheck.summary?.subscribed_apps_count > 0 ? "text-emerald-300" : "text-red-300"}>
                      {subCheck.summary?.subscribed_apps_count ?? "—"}
                    </span>
                  </div>
                  <div className="text-[10px] text-white/45 font-mono-data">
                    WABA ID: {subCheck.waba_id || "—"} · Phone ID: {subCheck.phone_number_id || "—"} · API: {subCheck.graph_api_version}
                  </div>
                </div>
              </div>
              <div className="border border-[#222] bg-black/30 px-3 py-2" data-testid="meta-subscription-advisory">
                <div className="text-[10px] font-display tracking-widest uppercase text-white/55 mb-1">Advisory</div>
                <div className="text-[11px] text-amber-200/85 leading-snug whitespace-pre-line">
                  {subCheck.advisory || "—"}
                </div>
              </div>
            </div>

            {/* Subscribed apps list */}
            {subCheck.summary?.subscribed_apps?.length > 0 && (
              <div className="border border-[#222] bg-black/30 px-3 py-2" data-testid="meta-subscription-apps">
                <div className="text-[10px] font-display tracking-widest uppercase text-white/55 mb-1.5">
                  Subscribed Apps ({subCheck.summary.subscribed_apps.length})
                </div>
                <ul className="space-y-1 text-[11px]">
                  {subCheck.summary.subscribed_apps.map((a, i) => (
                    <li key={i} className="font-mono-data text-white/80">
                      <span className="text-white/45">app_id=</span>{a.app_id || "—"}
                      <span className="text-white/45"> · name=</span>{a.app_name || "—"}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Each check raw */}
            {(subCheck.checks || []).map((chk) => (
              <div
                key={chk.name}
                data-testid={`meta-check-${chk.name}`}
                className="border border-[#222] bg-black/30"
              >
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#1A1A1A]">
                  <div className="text-[10px] font-display tracking-widest uppercase text-white/65">
                    {chk.name}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 text-[10px] font-mono-data tracking-widest uppercase border ${
                      chk.ok ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                             : "border-red-500/40 text-red-300 bg-red-500/10"
                    }`}>
                      HTTP {chk.http_status ?? "—"}
                    </span>
                    {chk.error && (
                      <span className="text-[10px] text-red-300/85 font-mono-data">{chk.error}</span>
                    )}
                  </div>
                </div>
                <pre className="p-3 text-[10px] text-white/75 font-mono-data overflow-x-auto whitespace-pre-wrap break-words max-h-72">
{JSON.stringify(chk.body, null, 2)}
                </pre>
              </div>
            ))}

            {/* Full raw JSON */}
            <details className="border border-[#222] bg-black/30 px-3 py-2" data-testid="meta-subscription-raw">
              <summary className="text-[10px] font-display tracking-widest uppercase text-white/55 cursor-pointer">
                Full raw JSON
              </summary>
              <pre className="text-[10px] text-white/70 font-mono-data overflow-x-auto whitespace-pre-wrap break-words mt-2 max-h-96">
{JSON.stringify(subCheck, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {!subCheck && !subError && !subLoading && (
          <div className="text-[11px] text-white/40 italic">
            Click <span className="text-white/65">Run Meta Subscription Check</span> to call Meta Graph API and see the live result.
          </div>
        )}
      </Section>

      {/* Inbound Webhook Debug — diagnoses missing inbound message storage */}
      <Section
        title="Inbound Webhook Debug"
        action={
          <button
            data-testid="inbound-debug-btn"
            onClick={runInboundDebug}
            disabled={inboundLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display tracking-widest uppercase border border-[#FF1E1E] text-[#FF1E1E] hover:bg-[#FF1E1E]/10 disabled:opacity-50"
          >
            {inboundLoading ? "Loading…" : "Inspect Last Inbound POSTs"}
          </button>
        }
      >
        <div className="text-[11px] text-white/55 mb-3 leading-snug">
          Read-only. Re-runs the inbound parser against the last 5 raw Meta POST payloads and shows whether each parsed
          message found its CRM lead by phone, whether the wa_message_id is already in the lead conversation, and any
          alternate phone matches. Pinpoints the failure stage (parse / phone-normalise / lead-lookup / dedupe).
        </div>

        {inboundError && (
          <div data-testid="inbound-debug-error" className="border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {String(inboundError)}
          </div>
        )}

        {inbound && (
          <div className="space-y-3" data-testid="inbound-debug-result">
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/55">
              Events stored total: <span className="text-white/85">{inbound.total_events_stored}</span> · Returned: {inbound.events_returned}
            </div>
            {inbound.advisory && (
              <div className="border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200/85 leading-snug whitespace-pre-line">
                {inbound.advisory}
              </div>
            )}

            {inbound.events_returned === 0 && (
              <div className="border border-red-500/40 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
                Zero raw events stored. Meta has not POSTed any payload yet (signature verification may also be rejecting them — check backend logs for `HMAC signature verification FAILED`).
              </div>
            )}

            {inbound.events.map((ev, i) => (
              <div key={ev.event_id || i} className="border border-[#222] bg-black/30" data-testid={`inbound-event-${i}`}>
                <div className="px-3 py-2 border-b border-[#1A1A1A] flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[10px] font-mono-data text-white/55">
                    #{i + 1} · {ev.received_at?.slice(0, 19).replace("T", " ")} · event_id={ev.event_id?.slice(0, 8)}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* NEW verdict pills */}
                    <Pill ok={ev.signature_ok} label={ev.signature_ok ? "signature OK" : "signature FAIL"} />
                    <Pill ok={ev.parse_ok}    label={ev.parse_ok    ? "json OK"     : "json FAIL"} />
                    <Pill ok={ev.enabled}     label={ev.enabled     ? "enabled"     : "disabled"} />
                    <Pill ok={ev.processed}   label={ev.processed   ? "processed"   : "not processed"} />
                  </div>
                </div>
                <div className="p-3 space-y-2">
                  {/* Top-line verdict reason if rejected */}
                  {ev.signature_ok === false && (
                    <div className="border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-[11px] text-red-300 leading-snug">
                      <span className="font-display tracking-widest uppercase font-bold">HMAC signature mismatch</span>
                      {" — "}body received {ev.raw_body_length ?? 0} bytes, X-Hub-Signature-256 header {ev.headers_signature_present ? "present" : "MISSING"}.
                      Almost always: the production <span className="font-mono-data">META_APP_SECRET</span> env var does not match the
                      App Secret of the Meta App that is sending the webhook. Re-copy the App Secret from Meta App Dashboard → Settings → Basic
                      and ensure no surrounding whitespace/quotes.
                    </div>
                  )}
                  {ev.production_safe === false && ev.production_unsafe_reason && (
                    <div className="border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-[11px] text-red-300 leading-snug">
                      <span className="font-display tracking-widest uppercase font-bold">Production unsafe</span>
                      {" — "}{ev.production_unsafe_reason}
                    </div>
                  )}
                  {/* Raw body text (only for parse failures) */}
                  {ev.raw_body_text && (
                    <details className="border border-[#1A1A1A] bg-black/40 px-2 py-1.5">
                      <summary className="text-[10px] font-mono-data tracking-widest uppercase text-amber-300/85 cursor-pointer">
                        Raw body text (parse failed)
                      </summary>
                      <pre className="text-[10px] text-white/75 font-mono-data overflow-x-auto whitespace-pre-wrap break-words mt-2 max-h-72">
{ev.raw_body_text}
                      </pre>
                    </details>
                  )}
                  {/* Shape summary */}
                  <div className="text-[10px] font-mono-data text-white/65 grid sm:grid-cols-2 gap-x-3 gap-y-1">
                    <div>field = <span className="text-white/85">{ev.shape?.field}</span></div>
                    <div>phone_number_id = <span className="text-white/85">{ev.shape?.metadata_phone_number_id || "—"}</span></div>
                    <div>display_phone = <span className="text-white/85">{ev.shape?.metadata_display_phone || "—"}</span></div>
                    <div>contact_wa_id = <span className="text-white/85">{ev.shape?.first_contact_wa_id || "—"}</span></div>
                    <div>contact_profile = <span className="text-white/85">{ev.shape?.first_contact_profile || "—"}</span></div>
                    <div>message_types = <span className="text-white/85">{(ev.shape?.message_types || []).join(", ") || "—"}</span></div>
                  </div>

                  {ev.parse_error && (
                    <div className="text-[11px] text-red-300/85 font-mono-data">parse_error: {ev.parse_error}</div>
                  )}

                  {/* Per-message diagnostic */}
                  {ev.per_message?.length > 0 && ev.per_message.map((pm, j) => (
                    <div key={j} className="border border-[#1A1A1A] bg-black/40 p-2 text-[11px]" data-testid={`inbound-event-${i}-msg-${j}`}>
                      <div className="font-mono-data text-white/75 break-all">
                        wa_message_id: <span className="text-white/95">{pm.wa_message_id || "—"}</span>
                      </div>
                      <div className="font-mono-data text-white/65">
                        type=<span className="text-white/85">{pm.type}</span>{" · "}
                        from_phone_raw=<span className="text-white/85">{pm.from_phone_raw || "—"}</span>{" · "}
                        normalised=<span className="text-white/85">{pm.phone_normalised || "—"}</span>
                      </div>
                      {pm.normalise_error && (
                        <div className="text-red-300/85 font-mono-data">normalise_error: {pm.normalise_error}</div>
                      )}
                      {pm.text && (
                        <div className="text-white/55 italic">&ldquo;{pm.text}&rdquo;</div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap mt-1.5">
                        <Pill ok={pm.lead_found} label={pm.lead_found ? "Lead found" : "Lead NOT found"} />
                        {pm.lead_found && pm.conversation_has_this_wamid !== null && (
                          <Pill ok={pm.conversation_has_this_wamid}
                                label={pm.conversation_has_this_wamid ? "wamid in conversation" : "wamid MISSING from conversation"} />
                        )}
                      </div>
                      {pm.lead && (
                        <div className="text-[10px] font-mono-data text-white/65 mt-1">
                          lead_id={pm.lead.lead_id?.slice(0, 8)}… · lead_phone={pm.lead.lead_phone} · last_inbound_at={pm.lead.last_inbound_at?.slice(0, 19).replace("T", " ") || "—"} · convo_size={pm.lead.conversation_total}
                        </div>
                      )}
                      {pm.alt_phone_matches?.length > 0 && (
                        <div className="text-[10px] text-amber-300/85 font-mono-data mt-1">
                          alt_phone_matches: {pm.alt_phone_matches.map(a => `${a.id?.slice(0,8)}…/${a.phone}`).join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <details className="border border-[#222] bg-black/30 px-3 py-2" data-testid="inbound-debug-raw">
              <summary className="text-[10px] font-display tracking-widest uppercase text-white/55 cursor-pointer">
                Full raw JSON
              </summary>
              <pre className="text-[10px] text-white/70 font-mono-data overflow-x-auto whitespace-pre-wrap break-words mt-2 max-h-96">
{JSON.stringify(inbound, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {!inbound && !inboundError && !inboundLoading && (
          <div className="text-[11px] text-white/40 italic">
            Click <span className="text-white/65">Inspect Last Inbound POSTs</span> to view the last 5 webhook payloads
            and the lead-lookup result for each parsed message.
          </div>
        )}
      </Section>

      {/* Template checklist */}
      {checklist && (
        <Section title="Template Approval Checklist">
          <div className="text-[11px] text-white/55 mb-3">
            Track Meta template approval state manually. UI sends are gated against these templates server-side.
          </div>
          <div className="space-y-2">
            {checklist.required_templates.map((name) => {
              const entry = checklist.items[name] || { state: "not_submitted" };
              const tone = STATE_TONE[entry.state] || STATE_TONE.not_submitted;
              return (
                <div
                  key={name}
                  className="border border-[#222] bg-black/30 px-3 py-3"
                  data-testid={`template-row-${name}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-mono-data text-xs text-white/90">{name}</div>
                    <div className={`px-2 py-0.5 text-[10px] font-mono-data tracking-widest uppercase border ${tone}`}>
                      {entry.state}
                    </div>
                  </div>
                  {entry.updated_at && (
                    <div className="text-[10px] text-white/40 font-mono-data mt-1">
                      Updated {entry.updated_at} · by {entry.updated_by_name || entry.updated_by || "—"}
                      {entry.note ? ` · ${entry.note}` : ""}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {TEMPLATE_STATES.map((s) => (
                      <button
                        key={s}
                        data-testid={`template-state-${name}-${s}`}
                        onClick={() => updateTemplate(name, s)}
                        disabled={savingTpl === name || entry.state === s}
                        className={`px-2 py-1 text-[10px] font-display tracking-widest uppercase border transition-colors disabled:opacity-50 ${
                          entry.state === s
                            ? "border-[#FF1E1E] text-[#FF1E1E] bg-[#FF1E1E]/10 cursor-default"
                            : "border-[#2A2A2A] text-white/55 hover:text-white hover:border-white/40"
                        }`}
                      >
                        {s.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Recent send audit */}
      {audit && (
        <Section title={`Recent Send Audit (${audit.count})`}>
          {audit.items.length === 0 ? (
            <div className="text-xs text-white/40 italic">No send attempts recorded yet.</div>
          ) : (
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full text-[11px] min-w-[720px]" data-testid="send-audit-table">
                <thead>
                  <tr className="text-left text-[10px] font-display tracking-widest uppercase text-white/45 border-b border-[#222]">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Actor</th>
                    <th className="py-2 pr-3">Phone</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Mode</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.items.map((it, i) => {
                    const isErr = (it.status || "").startsWith("blocked") || it.status === "failed";
                    return (
                      <tr key={i} data-testid={`audit-row-${i}`} className="border-b border-[#1A1A1A] align-top">
                        <td className="py-2 pr-3 font-mono-data text-white/55 whitespace-nowrap">{it.created_at?.slice(0, 19).replace("T", " ")}</td>
                        <td className="py-2 pr-3 text-white/70">{it.actor_name || it.actor_phone || "—"}</td>
                        <td className="py-2 pr-3 font-mono-data text-white/75">{it.phone || "—"}</td>
                        <td className="py-2 pr-3 text-white/70">{it.message_type}{it.template_name ? <span className="text-white/45 font-mono-data"> · {it.template_name}</span> : null}</td>
                        <td className="py-2 pr-3"><span className={`px-1.5 py-0.5 text-[10px] font-mono-data tracking-widest uppercase border ${MODE_TONE[it.mode]?.cls || "border-white/20 text-white/60"}`}>{MODE_TONE[it.mode]?.label || it.mode}</span></td>
                        <td className={`py-2 pr-3 font-mono-data ${isErr ? "text-red-300" : "text-emerald-300"}`}>{it.status}</td>
                        <td className="py-2 pr-3 text-white/55">
                          {it.error ? <span className="text-red-300/85">{it.error}</span> : (it.meta_message_id || "—")}
                          {it.canary_decision && it.canary_decision !== "dry_run" && (
                            <div className="text-[10px] text-white/40">canary: {it.canary_decision}</div>
                          )}
                          {it.window_decision && (
                            <div className="text-[10px] text-white/40">window: {it.window_decision}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {/* Gupshup snippet (read-only) */}
      {status && (
        <Section title="Gupshup (untouched)">
          <div className="text-[11px] text-white/55 mb-2">
            Separate channel used only for internal operator alerts. Phase 9.1 does not alter Gupshup behaviour.
          </div>
          <div className="flex items-center gap-2">
            <Pill ok={!!status.gupshup?.configured} label={status.gupshup?.configured ? "Configured" : "Not configured"} />
            <span className="text-[11px] text-white/55 font-mono-data">
              {Object.entries(status.gupshup || {}).filter(([k]) => k !== "configured").map(([k, v]) => `${k}=${String(v)}`).join(" · ") || "—"}
            </span>
          </div>
        </Section>
      )}

      {loading && !status && (
        <div className="text-xs text-white/45" data-testid="wa-cloud-loading">Loading…</div>
      )}
    </div>
  );
}
